// Sentry-Init für das Frontend (Task #44).
//
// Lazy-aktiv: nur wenn VITE_SENTRY_DSN gesetzt ist, wird das SDK
// initialisiert. Lokale Dev-Setups ohne DSN bleiben komplett ohne
// Sentry-Calls — analog zu Plausible (siehe lib/analytics.js).
//
// Komplementär zu lib/errorReporter.js:
//   - errorReporter: dedupliziert + Plausible-Count-Events (keine PII)
//   - sentry: voller Stack-Trace mit Source-Maps, Release-Tracking,
//             nach clientseitigem PII-Scrubbing
//
// Beide laufen parallel — wenn ein Window-Error feuert, geht ein
// Plausible-Event hoch UND Sentry sieht denselben Error via seine
// integrierten Browser-Handler. Doppelte Erfassung ist gewollt:
// Plausible für „wie oft", Sentry für „warum".

import * as Sentry from '@sentry/react';
import { isReloadPending } from './chunkReload';

let initialized = false;

/**
 * Liefert true, wenn Sentry tatsächlich initialisiert wurde.
 * Andere Module können das nutzen, um z.B. `Sentry.setUser()` nur
 * bei aktivem SDK zu rufen.
 */
export function isSentryActive() {
  return initialized;
}

/**
 * Fehler, die den Nutzer nicht aufhalten (z. B. Aufräumen im Speicher),
 * still an Sentry melden. Ohne aktives Sentry passiert nichts.
 */
export function reportError(err, extra = {}) {
  if (!isSentryActive()) return;
  const error = err instanceof Error ? err : new Error(String(err?.message || err));
  Sentry.captureException(error, { extra });
}

/**
 * Initialisiert Sentry, wenn eine DSN in der ENV liegt.
 * Muss vor allem anderen App-Code laufen — main.jsx ist der richtige Ort.
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    // Kein DSN = kein Sentry. Kein Warn-Log, weil das lokale
    // Standard-Setup ohne Sentry ist; ein Log würde nur Lärm machen.
    return;
  }
  if (initialized) return;

  const release = import.meta.env.VITE_APP_RELEASE
    || import.meta.env.VITE_APP_VERSION
    || undefined;
  const environment = import.meta.env.MODE === 'production' ? 'production' : 'development';

  Sentry.init({
    dsn,
    release,
    environment,

    // Performance-Tracing zunächst deaktiviert — wir sind Pre-Launch
    // mit kleinem Free-Tier-Budget, jeder Sample kostet ein Event.
    // Bei Bedarf später schrittweise hochdrehen.
    tracesSampleRate: 0,

    // Session-Replay erstmal aus. Replay würde Maus-Bewegungen +
    // Klicks aufzeichnen und ist DSGVO-rechtlich der heikelste Teil
    // von Sentry — bewusst nicht aktivieren, bis wir das mit Aufwand
    // sauber dokumentiert + clientseitiges Masking konfiguriert haben.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,

    // ─── PII-Scrubbing (clientseitig, vor jedem Send) ────────────
    // Wichtig: das passiert auf dem User-Gerät BEVOR das Event Sentry
    // erreicht. Server-Side Scrubbing in der Sentry-UI ist die zweite
    // Linie; clientseitig ist die rechtlich relevante.
    sendDefaultPii: false,
    beforeSend(event) {
      return filterEvent(event);
    },
    beforeBreadcrumb(breadcrumb) {
      // URLs in Breadcrumbs (z.B. navigation) können Project-/Calc-IDs
      // enthalten. Wir behalten die Route, ersetzen aber UUID-artige
      // Segmente durch `:id`-Platzhalter.
      if (breadcrumb.data?.url) {
        breadcrumb.data.url = scrubUrl(breadcrumb.data.url);
      }
      if (breadcrumb.data?.to) {
        breadcrumb.data.to = scrubUrl(breadcrumb.data.to);
      }
      if (breadcrumb.data?.from) {
        breadcrumb.data.from = scrubUrl(breadcrumb.data.from);
      }
      return breadcrumb;
    }
  });

  initialized = true;
}

// ─── Scrubbing-Helpers ──────────────────────────────────────────────

// UUID-Pattern (Supabase + unsere generierten IDs). Plus die kurzen
// 9-Zeichen-IDs aus elementShape.js' generateId.
const UUID_RX = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const SHORT_ID_RX = /\b[a-z0-9]{9}\b/gi;
const EMAIL_RX = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi;

function scrubString(s) {
  if (typeof s !== 'string') return s;
  return s
    .replace(UUID_RX, ':uuid')
    .replace(EMAIL_RX, ':email')
    // SHORT_ID nur in URL-Pfaden, sonst trifft das normale Wörter —
    // wird in scrubUrl spezifisch angewendet.
    ;
}

function scrubUrl(url) {
  if (typeof url !== 'string') return url;
  // UUIDs + E-Mails + 9-Char-IDs in URL-Pfaden ersetzen.
  return url
    .replace(UUID_RX, ':uuid')
    .replace(EMAIL_RX, ':email')
    .replace(/\/[a-z0-9]{9}(?=\/|$|\?)/gi, '/:id');
}

/**
 * beforeSend: verwirft Folgefehler, solange die App nach einem Deploy neu
 * lädt (lib/chunkReload.js), sonst PII-Scrubbing.
 */
export function filterEvent(event) {
  if (isReloadPending()) return null;
  return scrubEvent(event);
}

function scrubEvent(event) {
  // Request-URL + Referer
  if (event.request) {
    if (event.request.url) event.request.url = scrubUrl(event.request.url);
    if (event.request.headers?.Referer) {
      event.request.headers.Referer = scrubUrl(event.request.headers.Referer);
    }
    // Cookies + Auth-Header rausnehmen — Default sendDefaultPii=false
    // sollte das schon machen, aber doppelt hält besser.
    if (event.request.cookies) event.request.cookies = '[scrubbed]';
    if (event.request.headers) {
      delete event.request.headers.Authorization;
      delete event.request.headers.Cookie;
    }
  }

  // User-Identifier (Supabase auth.user.id) wird von Sentry-Integrations
  // nicht automatisch gesendet, weil sendDefaultPii=false. Falls wir
  // später `Sentry.setUser({ id })` rufen, hier nochmal absichern:
  if (event.user) {
    if (event.user.email) event.user.email = '[scrubbed]';
    if (event.user.ip_address) event.user.ip_address = '[scrubbed]';
    // user.id behalten wir bewusst — ist nur eine Supabase-UUID,
    // keine direkt identifizierende Information, hilft beim Triage
    // („alle Errors von User X kommen aus Pfad Y").
  }

  // Exception-Messages auf E-Mails + UUIDs scrubben
  if (event.exception?.values) {
    for (const ex of event.exception.values) {
      if (ex.value) ex.value = scrubString(ex.value);
    }
  }

  // Breadcrumb-Messages
  if (event.breadcrumbs) {
    for (const bc of event.breadcrumbs) {
      if (bc.message) bc.message = scrubString(bc.message);
    }
  }

  return event;
}
